import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { type HospitalDataContextType, useHospitalData } from "../contexts/HospitalDataProvider";
import { backendService } from "../services/backend.service";
import type { DepartmentStaffShiftStats, UsualShift } from "../utils/models";
import { staffShiftStatsKeys } from "./scheduleCache";
import { useRosterLookup } from "./useRosterLookup";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";

const SECOND_MS = 1000;
// Schedule writes mark the counts stale; this only covers changes nobody here made.
const STAFF_SHIFT_STATS_STALE_TIME_MS = 60 * SECOND_MS;
// DEFAULT: someone with no shifts, in a role and department with none either, starts on a 6a–6p shift with no location,
// as the panels always offered.
const NO_USUAL_SHIFTS: UsualShift[] = [{ startTime: "06:00", endTime: "18:00", location: "" }];
const noRefetch = (): void => undefined;

export type StaffShiftCounts = {
	/** A person's two most common hours and location, else their role's, else the department's, else 6a–6p. */
	staffShiftDefaults: HospitalDataContextType["calculateDefaultTimesAndLocationForStaff"];
	/** A person's Saturday, Sunday and Friday Night shifts. */
	nurseWeekendShifts: (nurseId: number) => number;
	/** With the cache on only: a person's scheduled shifts and charge shifts (off, the Summary tab counts the old list). */
	nurseShifts: (nurseId: number) => number;
	nurseChargeShifts: (nurseId: number) => number;
	isLoading: boolean;
	isError: boolean;
	refetch: () => void;
};

/**
 * The per-person counts the staffing panels show (usual hours to prefill a new shift, weekend shifts to rank who to
 * move), over the weeks `department_data` returns an admin. With `staffing_schedule_cache` on the server counts them,
 * loaded when a panel needing them opens; off, the provider counts the old list, as before.
 */
export const useStaffShiftCounts = (): StaffShiftCounts => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const { calculateDefaultTimesAndLocationForStaff, getNurseWeekendShifts } = useHospitalData();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const { getNurseById } = useRosterLookup(departmentId);
	const stats = useQuery<DepartmentStaffShiftStats>({
		queryKey: staffShiftStatsKeys.department(departmentId),
		queryFn: async () => (await backendService.getStaffShiftStats(departmentId)).data,
		enabled: isCacheEnabled,
		staleTime: STAFF_SHIFT_STATS_STALE_TIME_MS,
	});
	const byNurseId = useMemo(() => new Map(stats.data?.staff.map((person) => [person.nurseId, person])), [stats.data]);

	const staffShiftDefaults = useCallback(
		(staffId: number): UsualShift[] => {
			const own = byNurseId.get(staffId)?.usualShifts;
			if (own?.length) return own;
			const role = getNurseById(staffId)?.role;
			const roleShifts = role ? stats.data?.usualShiftsByRole[role] : undefined;
			if (roleShifts?.length) return roleShifts;
			return stats.data?.usualShifts.length ? stats.data.usualShifts : NO_USUAL_SHIFTS;
		},
		[byNurseId, getNurseById, stats.data],
	);
	// DEFAULT: 0 for someone without a scheduled shift in the weeks, as the panels counted them.
	const nurseWeekendShifts = useCallback((nurseId: number): number => byNurseId.get(nurseId)?.weekendShifts ?? 0, [byNurseId]);
	// DEFAULT: 0, as above.
	const nurseShifts = useCallback((nurseId: number): number => byNurseId.get(nurseId)?.shifts ?? 0, [byNurseId]);
	// DEFAULT: 0, as above.
	const nurseChargeShifts = useCallback((nurseId: number): number => byNurseId.get(nurseId)?.chargeShifts ?? 0, [byNurseId]);

	if (!isCacheEnabled) {
		return {
			staffShiftDefaults: calculateDefaultTimesAndLocationForStaff,
			nurseWeekendShifts: getNurseWeekendShifts,
			nurseShifts,
			nurseChargeShifts,
			isLoading: false,
			isError: false,
			refetch: noRefetch,
		};
	}
	return { staffShiftDefaults, nurseWeekendShifts, nurseShifts, nurseChargeShifts, isLoading: stats.isLoading, isError: stats.isError, refetch: stats.refetch };
};
